<script setup lang="ts">
import Icon from '@components/Icon.vue';
import type { Color } from '@global-types/colors.ts';
import OtherFiltersFilter from '@pages/search/components/filters/OtherFiltersFilter.vue';
import { useFilterStore } from '@stores/useFilterStore.ts';
import { storeToRefs } from 'pinia';
import { trans } from '@tools/translation.ts';
import {
  IconBuildings,
  IconUsers,
  IconMapPin,
  IconSlidersFill,
  IconCoins,
  IconRuler,
} from '@icons';
import DesktopFilter from '@pages/search/components/filters/DesktopFilter.vue';
import TeamSizeFilter from '@pages/search/components/filters/TeamSizeFilter.vue';
import BudgetFilter from '@pages/search/components/filters/BudgetFilter.vue';
import SurfaceFilter from '@pages/search/components/filters/SurfaceFilter.vue';
import OfficeTypeFilter from '@pages/search/components/filters/OfficeTypeFilter.vue';
import { FilterType } from '@enums/FilterType.ts';
import LocationFilter from '@pages/search/components/filters/LocationFilter.vue';

const filterStore = useFilterStore();
const { openedFilter } = storeToRefs(filterStore);
const { setDesktopFilter } = filterStore;

const isFilterActive = (filterType: FilterType): boolean => openedFilter.value === filterType;

const getIconColor = ((filterType: FilterType): Color => (isFilterActive(filterType) ? 'blue' : 'gray-dark'));
</script>

<template>
  <DesktopFilter
    :is-open="isFilterActive(FilterType.LOCATION)"
    @update:is-open="(isOpen) => setDesktopFilter(isOpen ? FilterType.LOCATION : null)"
  >
    <template #label>
      {{ trans('search.filters.location.title') }}
    </template>

    <template #icon>
      <Icon
        :svg="IconMapPin"
        :fill="getIconColor(FilterType.LOCATION)"
        height="24"
        width="24"
      />
    </template>

    <template #panel>
      <LocationFilter class="w-[478px]" />
    </template>
  </DesktopFilter>

  <DesktopFilter
    :is-open="isFilterActive(FilterType.CAPACITY)"
    @update:is-open="(isOpen) => setDesktopFilter(isOpen ? FilterType.CAPACITY : null)"
  >
    <template #label>
      {{ trans('search.filters.capacity.title') }}
    </template>

    <template #icon>
      <Icon
        :svg="IconUsers"
        :fill="getIconColor(FilterType.CAPACITY)"
        height="24"
        width="24"
      />
    </template>

    <template #panel>
      <TeamSizeFilter />
    </template>
  </DesktopFilter>

  <DesktopFilter
    :is-open="isFilterActive(FilterType.OFFICE_TYPE)"
    @update:is-open="(isOpen) => setDesktopFilter(isOpen ? FilterType.OFFICE_TYPE : null)"
  >
    <template #label>
      {{ trans('search.filters.office_type.title') }}
    </template>

    <template #icon>
      <Icon
        :svg="IconBuildings"
        :fill="getIconColor(FilterType.OFFICE_TYPE)"
        height="24"
        width="24"
      />
    </template>

    <template #panel>
      <OfficeTypeFilter />
    </template>
  </DesktopFilter>

  <DesktopFilter
    :is-open="isFilterActive(FilterType.BUDGET)"
    @update:is-open="(isOpen) => setDesktopFilter(isOpen ? FilterType.BUDGET : null)"
  >
    <template #label>
      {{ trans('search.filters.budget.title') }}
    </template>

    <template #icon>
      <Icon
        :svg="IconCoins"
        :stroke="getIconColor(FilterType.BUDGET)"
        height="24"
        width="24"
      />
    </template>

    <template #panel>
      <BudgetFilter />
    </template>
  </DesktopFilter>

  <DesktopFilter
    :is-open="isFilterActive(FilterType.SURFACE)"
    @update:is-open="(isOpen) => setDesktopFilter(isOpen ? FilterType.SURFACE : null)"
  >
    <template #label>
      {{ trans('search.filters.surface.title') }}
    </template>

    <template #icon>
      <Icon
        :svg="IconRuler"
        :fill="getIconColor(FilterType.SURFACE)"
        height="24"
        width="24"
      />
    </template>

    <template #panel>
      <SurfaceFilter />
    </template>
  </DesktopFilter>

  <DesktopFilter
    :is-open="isFilterActive(FilterType.OTHER_FILTERS)"
    @update:is-open="(isOpen) => setDesktopFilter(isOpen ? FilterType.OTHER_FILTERS : null)"
  >
    <template #icon>
      <Icon
        :svg="IconSlidersFill"
        :fill="getIconColor(FilterType.OTHER_FILTERS)"
        height="24"
        width="24"
      />
    </template>

    <template #label>
      {{ trans('search.filters.other_filters.title') }}
    </template>

    <template #panel>
      <OtherFiltersFilter class="w-[548px] overflow-y-auto overflow-x-hidden" />
    </template>
  </DesktopFilter>
</template>
